import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import assetService from '../services/assetService';
import complianceService from '../services/complianceService';
import maintenanceService from '../services/maintenanceService';
import { API_ENDPOINTS } from '../config/api';
import '../styles/AddAsset.css';
import AssistantSidebar from './AssistantSidebar';

const AddAsset = ({ onAssetAdded }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const token = sessionStorage.getItem('token');

  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState('');
  
  // Tab state - 7 comprehensive tabs
  const [currentTab, setCurrentTab] = useState(0);
  const tabs = [
    { id: 0, label: 'Basic Information', icon: '📋' },
    { id: 1, label: 'Inventory', icon: '📦' },
    { id: 2, label: 'Asset Images', icon: '📷' },
    { id: 3, label: 'Purchase & Warranty', icon: '💳' },
    { id: 4, label: 'Status & Condition', icon: '✓' },
    { id: 5, label: 'Maintenance', icon: '🔧' },
    { id: 6, label: 'Compliance', icon: '⚖️' }
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(API_ENDPOINTS.LAB_ADMIN_ASSET_CATEGORIES, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setCategories(data);
      } catch (err) {
        // Error fetching categories
      }
    };

    const fetchDepartments = async () => {
      try {
        const res = await axios.get(API_ENDPOINTS.DEPARTMENTS, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setDepartments(data);
      } catch (err) {
        // Error fetching departments
      }
    };

    fetchCategories();
    fetchDepartments();
  }, [token]);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!selectedCategoryId) {
        setSubCategories([]);
        setSelectedSubCategoryId('');
        setSpecifications([]);
        return;
      }

      try {
        const res = await axios.get(`${API_ENDPOINTS.LAB_ADMIN_SUBCATEGORIES}/${selectedCategoryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setSubCategories(data);
      } catch (err) {
        // Error fetching subcategories
        setSubCategories([]);
      }
    };

    fetchSubCategories();
  }, [selectedCategoryId, token]);

  useEffect(() => {
    const fetchSpecifications = async () => {
      if (!selectedSubCategoryId) {
        setSpecifications([]);
        return;
      }

      try {
        const res = await axios.get(`${API_ENDPOINTS.LAB_ADMIN_SPECIFICATIONS}/${selectedSubCategoryId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setSpecifications(data);
        
        const initialSpecs = {};
        data.forEach(spec => {
          initialSpecs[spec._id] = '';
        });
        setFormData(prev => ({
          ...prev,
          specificationValues: initialSpecs
        }));
      } catch (err) {
        // Error fetching specifications
        setSpecifications([]);
      }
    };

    fetchSpecifications();
  }, [selectedSubCategoryId, token]);

  const [formData, setFormData] = useState({
    assetName: '',
    category: '',
    subCategory: '',
    assetType: 'non-consumable',
    brand: '',
    model: '',
    serialNumber: '',
    quantity: 1,
    minQuantity: 0,
    department: '',
    purchaseDate: '',
    purchaseCost: '',
    warrantyExpiryDate: '',
    expiryDate: '',
    complianceDate: '',
    status: 'available',
    condition: 'good',
    supplier: '',
    invoiceNumber: '',
    invoiceImage: '',
    maintenanceFrequency: '',
    maintenanceStartDate: '',
    maintenanceCycleMonths: null,
    maintenanceCycleYears: null,
    complianceFrequency: '',
    complianceCycleMonths: null,
    complianceCycleYears: null,
    complianceStartDate: '',
    notes: '',
    specificationValues: {},
    maintenanceChecklists: [],
    complianceChecklists: [],
    assetImage: ''
  });

  const [unitImages, setUnitImages] = useState([]);
  const [unitSpecs, setUnitSpecs] = useState([]);
  const [invoiceImagePreview, setInvoiceImagePreview] = useState('');
  const [assetImagePreview, setAssetImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [maintenanceChecklistItem, setMaintenanceChecklistItem] = useState('');
  const [complianceChecklistItem, setComplianceChecklistItem] = useState('');
  const [planLimitPopup, setPlanLimitPopup] = useState({
    visible: false,
    title: '',
    message: ''
  });

  const openPlanLimitPopup = (title, message) => {
    setPlanLimitPopup({ visible: true, title, message });
  };

  const closePlanLimitPopup = () => {
    setPlanLimitPopup((prev) => ({ ...prev, visible: false }));
  };

  const isPlanActionError = (payload = {}) => {
    const msg = String(payload?.message || '').toLowerCase();
    return Boolean(payload?.limitExceeded || payload?.expired) || /limit|plan|subscription|renew|upgrade/.test(msg);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'category') {
      const categoryId = categories.find(c => c.name === value)?._id || '';
      setSelectedCategoryId(categoryId);
      setSelectedSubCategoryId('');
      setFormData({
        ...formData,
        [name]: value,
        subCategory: '',
        specificationValues: {}
      });
    } else if (name === 'subCategory') {
      setSelectedSubCategoryId(value);
      setFormData({
        ...formData,
        [name]: value,
        specificationValues: {}
      });
    } else if (name === 'quantity') {
      const newQuantity = parseInt(value) || 1;
      const newUnitImages = Array(newQuantity).fill(null).map((_, i) => unitImages[i] || '');
      const newUnitSpecs = Array(newQuantity).fill(null).map((_, i) => unitSpecs[i] || {});
      setUnitImages(newUnitImages);
      setUnitSpecs(newUnitSpecs);
      setFormData({
        ...formData,
        [name]: newQuantity
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSpecificationChange = (specId, value) => {
    setFormData({
      ...formData,
      specificationValues: {
        ...formData.specificationValues,
        [specId]: value
      }
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          invoiceImage: reader.result
        });
        setInvoiceImagePreview(reader.result);
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData({
      ...formData,
      invoiceImage: ''
    });
    setInvoiceImagePreview('');
  };

  const handleAssetImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Asset image size should be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          assetImage: reader.result
        });
        setAssetImagePreview(reader.result);
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveAssetImage = () => {
    setFormData({
      ...formData,
      assetImage: ''
    });
    setAssetImagePreview('');
  };

  const handleUnitImageChange = (index, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const newUnitImages = [...unitImages];
      newUnitImages[index] = reader.result;
      setUnitImages(newUnitImages);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveUnitImage = (index) => {
    const newUnitImages = [...unitImages];
    newUnitImages[index] = '';
    setUnitImages(newUnitImages);
  };

  const handleUnitSpecChange = (unitIndex, specName, specValue) => {
    const newUnitSpecs = [...unitSpecs];
    if (!newUnitSpecs[unitIndex]) {
      newUnitSpecs[unitIndex] = {};
    }
    newUnitSpecs[unitIndex][specName] = specValue;
    setUnitSpecs(newUnitSpecs);
  };

  const handleAddMaintenanceChecklistItem = () => {
    if (maintenanceChecklistItem.trim()) {
      setFormData({
        ...formData,
        maintenanceChecklists: [
          ...formData.maintenanceChecklists,
          { text: maintenanceChecklistItem.trim(), completed: false }
        ]
      });
      setMaintenanceChecklistItem('');
    }
  };

  const handleRemoveMaintenanceChecklistItem = (index) => {
    setFormData({
      ...formData,
      maintenanceChecklists: formData.maintenanceChecklists.filter((_, i) => i !== index)
    });
  };

  const handleAddComplianceChecklistItem = () => {
    if (complianceChecklistItem.trim()) {
      setFormData({
        ...formData,
        complianceChecklists: [
          ...formData.complianceChecklists,
          { text: complianceChecklistItem.trim(), completed: false }
        ]
      });
      setComplianceChecklistItem('');
    }
  };

  const handleRemoveComplianceChecklistItem = (index) => {
    setFormData({
      ...formData,
      complianceChecklists: formData.complianceChecklists.filter((_, i) => i !== index)
    });
  };

  const handleSaveForm = () => {
    try {
      const dataToSave = {
        formData,
        unitImages,
        unitSpecs,
        selectedCategoryId,
        selectedSubCategoryId,
        maintenanceChecklistItem,
        complianceChecklistItem,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('assetFormDraft', JSON.stringify(dataToSave));
      toast.success('Form saved successfully!', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (err) {
      // Error saving form
      toast.error('Failed to save form', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const dataToSubmit = { ...formData, unitImages, unitSpecs };

      const frequencyToMonths = {
        'monthly': 1,
        'quarterly': 3,
        'semi_annual': 6,
        'yearly': 12
      };

      if (dataToSubmit.complianceFrequency && frequencyToMonths[dataToSubmit.complianceFrequency]) {
        dataToSubmit.complianceCycleMonths = frequencyToMonths[dataToSubmit.complianceFrequency];
        dataToSubmit.complianceCycleYears = null;
      }

      delete dataToSubmit.complianceFrequency;

      const maintenanceChecklists = dataToSubmit.maintenanceChecklists;
      const complianceChecklists = dataToSubmit.complianceChecklists;
      
      // NOTE: Don't delete checklists - they should be saved with the asset
      // delete dataToSubmit.maintenanceChecklists;
      // delete dataToSubmit.complianceChecklists;

      if (Object.keys(dataToSubmit.specificationValues).length > 0) {
        const convertedSpecs = {};
        specifications.forEach(spec => {
          if (dataToSubmit.specificationValues[spec._id]) {
            convertedSpecs[spec.name] = dataToSubmit.specificationValues[spec._id];
          }
        });
        dataToSubmit.specifications = convertedSpecs;
      }
      
      delete dataToSubmit.specificationValues;
      
  
      
      const response = await assetService.addAsset(dataToSubmit);
      const assetId = response.data.data._id;
      
      // FIX: Get user ID from multiple sources
      let userId = null;
      
      // Try from sessionStorage first
      const userData = sessionStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          userId = user?.id || user?._id;
        } catch (parseErr) {
          // Could not parse user data from sessionStorage
        }
      }
      
      // Fallback: get from localStorage
      if (!userId) {
        const localUserData = localStorage.getItem('user');
        if (localUserData) {
          try {
            const user = JSON.parse(localUserData);
            userId = user?.id || user?._id;
          } catch (parseErr) {
            // Could not parse user data from localStorage
          }
        }
      }
      
      if (!userId) {
        // Could not get user ID
      } else {
        // Using User ID
      }
      
      // Create compliance checklist
      if (complianceChecklists.length > 0 && formData.complianceStartDate && userId) {
        try {
          const checklistItems = complianceChecklists.map(item => ({
            text: item.text,
            completed: item.completed || false
          }));
          
          const dueDateObj = new Date(formData.complianceStartDate);
          if (isNaN(dueDateObj.getTime())) {
            // Invalid compliance start date
          } else {
            
            await complianceService.createCompliance(
              assetId,
              userId,
              checklistItems,
              dueDateObj.toISOString(),
              'medium',
              'Compliance checklist auto-generated during asset creation'
            );
            // Compliance checklist created successfully
          }
        } catch (checklistErr) {
          // Error creating compliance checklist
        }
      }
      
      // Create maintenance log/checklist
      if (maintenanceChecklists.length > 0 && formData.maintenanceStartDate && userId) {
        try {
          const checklistItems = maintenanceChecklists.map(item => ({
            text: item.text,
            completed: item.completed || false
          }));
          
          const dueDateObj = new Date(formData.maintenanceStartDate);
      
           
            
            await maintenanceService.createMaintenance({
              assetId,
              maintenanceType: 'preventive',
              checklist: checklistItems,
              sentToId: userId,
              priorityLevel: 'medium',
              expectedCompletionDate: dueDateObj.toISOString(),
              notes: 'Auto-generated during asset creation'
            });
        } catch (checklistErr) {
          // Error creating maintenance checklist
        }
      }
      
      toast.success('Asset added successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      
      if (onAssetAdded) {
        onAssetAdded(response.data.data);
      }

      setTimeout(() => {
        navigate('/assets');
      }, 1000);
    } catch (err) {
      const payload = err.response?.data || {};
      const errorMessage = payload.message || err.message || 'Failed to add asset';
      const errorDetails = payload.details || '';
      
     
      setError(errorMessage);
      if (isPlanActionError(payload)) {
        openPlanLimitPopup('Renew or Upgrade Plan', errorMessage);
      }
      
      const toastMessage = errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage;
      toast.error(toastMessage, {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-asset-page">
      <AssistantSidebar user={null} />
      <div className="add-asset-container">
        <h2>Add New Asset</h2>
      
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Tabs Header */}
        <div className="tabs-container-row">
          <div className="tabs-header">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${currentTab === tab.id ? 'active' : ''}`}
                onClick={() => setCurrentTab(tab.id)}
              >
                <span className="tab-icon">{tab.icon}</span>
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="asset-form">
          {/* Tab 0: Basic Information */}
          {currentTab === 0 && (
            <div className="form-section tab-content">
              <h3>📋 Basic Information</h3>
              
              <div className="form-group">
                <label htmlFor="assetName">Asset Name *</label>
                <input
                  type="text"
                  id="assetName"
                  name="assetName"
                  value={formData.assetName}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Oscilloscope"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                {selectedCategoryId && (
                  <div className="form-group">
                    <label htmlFor="subCategory">Sub-Category</label>
                    <select
                      id="subCategory"
                      name="subCategory"
                      value={formData.subCategory}
                      onChange={handleChange}
                    >
                      <option value="">Select a sub-category</option>
                      {subCategories.map(subCat => (
                        <option key={subCat._id} value={subCat._id}>{subCat.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="assetType">Asset Type *</label>
                  <select
                    id="assetType"
                    name="assetType"
                    value={formData.assetType}
                    onChange={handleChange}
                    required
                  >
                    <option value="non-consumable">Non-Consumable</option>
                    <option value="consumable">Consumable</option>
                  </select>
                </div>
              </div>

              {selectedSubCategoryId && specifications.length > 0 && (
                <div className="form-section" style={{ marginTop: '20px' }}>
                  <h3>📋 Specifications</h3>
                  <div className="specifications-grid">
                    {specifications.map(spec => (
                      <div key={spec._id} className="form-group">
                        <label htmlFor={`spec-${spec._id}`}>{spec.name}</label>
                        <input
                          type="text"
                          id={`spec-${spec._id}`}
                          placeholder={`e.g., 8GB`}
                          value={formData.specificationValues[spec._id] || ''}
                          onChange={(e) => handleSpecificationChange(spec._id, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="brand">Brand</label>
                  <input
                    type="text"
                    id="brand"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    placeholder="e.g., Agilent"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="model">Model</label>
                  <input
                    type="text"
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="e.g., DSO-X 2002A"
                  />
                </div>
              </div>

              {formData.assetType === 'non-consumable' && (
                <div className="form-group">
                  <label htmlFor="serialNumber">Serial Number</label>
                  <input
                    type="text"
                    id="serialNumber"
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleChange}
                    placeholder="e.g., SN123456"
                  />
                </div>
              )}
            </div>
          )}

          {/* Tab 1: Inventory Information */}
          {currentTab === 1 && (
            <div className="form-section tab-content">
              <h3>📦 Inventory Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="quantity">Quantity</label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    min="1"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="minQuantity">Min Quantity (Low Stock Alert)</label>
                  <input
                    type="number"
                    id="minQuantity"
                    name="minQuantity"
                    value={formData.minQuantity}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="department">Department *</label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a department</option>
                  {departments.map(dept => (
                    <option key={dept._id} value={dept.name}>{dept.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Tab 2: Asset Images */}
          {currentTab === 2 && (
            <div className="form-section tab-content">
              <h3>📷 Asset Image</h3>
              <p className="section-hint">Upload a single image of the complete asset (optional)</p>
              
              {assetImagePreview ? (
                <div className="image-preview-container">
                  <h4>Asset Image Preview</h4>
                  <div className="image-preview">
                    <img src={assetImagePreview} alt="Asset" />
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={handleRemoveAssetImage}
                      className="btn btn-sm btn-danger"
                    >
                      Remove Image
                    </button>
                    <label className="btn btn-sm btn-secondary" style={{ cursor: 'pointer', marginBottom: 0 }}>
                      Change Image
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAssetImageChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div style={{
                  border: '2px dashed #cbd5e0',
                  borderRadius: '8px',
                  padding: '40px',
                  textAlign: 'center',
                  backgroundColor: '#f7fafc',
                  cursor: 'pointer'
                }}>
                  <p style={{ margin: '0 0 10px', fontWeight: '500', color: '#4a5568' }}>
                    📸 Click to upload asset image
                  </p>
                  <small style={{ color: '#718096' }}>PNG, JPG, GIF up to 5MB</small>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAssetImageChange}
                    style={{ display: 'none' }}
                    id="assetImageInput"
                  />
                  <label htmlFor="assetImageInput" style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    cursor: 'pointer',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }} />
                </div>
              )}

              {/* Unit Images Section */}
              {formData.quantity > 0 && (
                <div style={{ marginTop: '25px' }}>
                  <h3 style={{ marginBottom: '15px' }}>📸 Unit Images (One image for each unit)</h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '15px',
                    padding: '10px'
                  }}>
                    {Array.from({ length: formData.quantity }).map((_, index) => (
                      <div key={index} style={{
                        border: '2px dashed #cbd5e0',
                        borderRadius: '8px',
                        padding: '15px',
                        textAlign: 'center',
                        backgroundColor: '#f7fafc'
                      }}>
                        <p style={{ margin: '0 0 10px', fontWeight: '600', color: '#2d3748' }}>
                          Unit {index + 1}
                        </p>
                        {unitImages[index] ? (
                          <div>
                            <img
                              src={unitImages[index]}
                              alt={`Unit ${index + 1}`}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '120px',
                                marginBottom: '8px',
                                borderRadius: '4px',
                                objectFit: 'cover'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveUnitImage(index)}
                              style={{
                                background: '#fed7d7',
                                color: '#c53030',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                marginRight: '5px'
                              }}
                            >
                              Remove
                            </button>
                            <label
                              style={{
                                background: '#bee3f8',
                                color: '#2c5282',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                display: 'inline-block'
                              }}
                            >
                              Change
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files[0] && handleUnitImageChange(index, e.target.files[0])}
                                style={{ display: 'none' }}
                              />
                            </label>
                          </div>
                        ) : (
                          <label style={{
                            display: 'block',
                            padding: '20px 10px',
                            cursor: 'pointer',
                            color: '#718096'
                          }}>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => e.target.files[0] && handleUnitImageChange(index, e.target.files[0])}
                              style={{ display: 'none' }}
                            />
                            <div style={{ fontSize: '24px', marginBottom: '5px' }}>📷</div>
                            <div style={{ fontSize: '12px' }}>Click to upload image</div>
                          </label>
                        )}
                        
                        {/* Per-unit specifications */}
                        {specifications.length > 0 && (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                            <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '600', color: '#4a5568' }}>
                              📋 Specs for Unit {index + 1}:
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px' }}>
                              {specifications.map(spec => (
                                <input
                                  key={`${index}-${spec._id}`}
                                  type="text"
                                  placeholder={`${spec.name}`}
                                  value={unitSpecs[index]?.[spec.name] || ''}
                                  onChange={(e) => handleUnitSpecChange(index, spec.name, e.target.value)}
                                  style={{
                                    fontSize: '11px',
                                    padding: '4px 6px',
                                    border: '1px solid #cbd5e0',
                                    borderRadius: '4px',
                                    outline: 'none'
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: '12px', color: '#718096', marginTop: '10px' }}>
                    Upload a unique image for each unit. Each image is max 5MB.
                    {specifications.length > 0 && ' Add different specs for each unit if they vary.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: Purchase & Warranty */}
          {currentTab === 3 && (
            <div className="form-section tab-content">
              <h3>💳 Purchase & Warranty Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="purchaseDate">Purchase Date</label>
                  <input
                    type="date"
                    id="purchaseDate"
                    name="purchaseDate"
                    value={formData.purchaseDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="purchaseCost">Purchase Cost</label>
                  <input
                    type="number"
                    id="purchaseCost"
                    name="purchaseCost"
                    value={formData.purchaseCost}
                    onChange={handleChange}
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="warrantyExpiryDate">Warranty Expiry Date</label>
                  <input
                    type="date"
                    id="warrantyExpiryDate"
                    name="warrantyExpiryDate"
                    value={formData.warrantyExpiryDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="expiryDate">Asset Expiry Date</label>
                  <input
                    type="date"
                    id="expiryDate"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="complianceDate">Compliance Due Date</label>
                  <input
                    type="date"
                    id="complianceDate"
                    name="complianceDate"
                    value={formData.complianceDate}
                    onChange={handleChange}
                    title="Date by which asset must be returned for compliance/maintenance check"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="supplier">Supplier</label>
                  <input
                    type="text"
                    id="supplier"
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleChange}
                    placeholder="e.g., Supplier Name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="invoiceNumber">Invoice Number</label>
                  <input
                    type="text"
                    id="invoiceNumber"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleChange}
                    placeholder="e.g., INV-001"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="invoiceImage">Invoice Image</label>
                  <input
                    type="file"
                    id="invoiceImage"
                    accept="image/*"
                    onChange={handleImageChange}
                    placeholder="Select invoice image"
                  />
                  <small className="help-text">Max size: 5MB (JPG, PNG, etc.)</small>
                </div>
              </div>

              {/* Invoice Image Preview */}
              {invoiceImagePreview && (
                <div className="image-preview-container">
                  <h4>Invoice Image Preview</h4>
                  <div className="image-preview">
                    <img src={invoiceImagePreview} alt="Invoice" />
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={handleRemoveImage}
                  >
                    Remove Image
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Status & Condition */}
          {currentTab === 4 && (
            <div className="form-section tab-content">
              <h3>✓ Status & Condition</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="available">Available</option>
                    <option value="in_use">In Use</option>
                    <option value="under_maintenance">Under Maintenance</option>
                    <option value="disposed">Disposed</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="condition">Condition</label>
                  <select
                    id="condition"
                    name="condition"
                    value={formData.condition}
                    onChange={handleChange}
                  >
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="damaged">Damaged</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Additional notes about the asset"
                  rows="4"
                />
              </div>
            </div>
          )}

          {/* Tab 5: Maintenance */}
          {currentTab === 5 && (
            <div className="form-section tab-content">
              <h3>🔧 Maintenance Schedule (For Non-Consumables)</h3>
              
              {formData.assetType === 'consumable' && (
                <p style={{ color: '#ef4444', fontWeight: '500', marginBottom: '1rem' }}>
                  ⓘ Maintenance schedules only apply to non-consumable assets.
                </p>
              )}

              {formData.assetType === 'non-consumable' && (
                <>
                  <p className="section-hint">Set how often this equipment needs servicing or calibration</p>

                  <div className="form-group">
                    <label>Maintenance Frequency</label>
                    <div className="frequency-options">
                      {[
                        { value: '', label: 'None' },
                        { value: 'weekly', label: 'Weekly (7 days)' },
                        { value: 'biweekly', label: 'Bi-Weekly (14 days)' },
                        { value: 'monthly', label: 'Monthly (30 days)' },
                        { value: 'quarterly', label: 'Quarterly (90 days)' },
                        { value: 'semi_annual', label: 'Semi-Annual (180 days)' },
                        { value: 'yearly', label: 'Yearly (365 days)' },
                        { value: 'custom', label: 'Custom' }
                      ].map(opt => (
                        <label key={opt.value} className={`frequency-chip ${formData.maintenanceFrequency === opt.value ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="maintenanceFrequency"
                            value={opt.value}
                            checked={formData.maintenanceFrequency === opt.value}
                            onChange={handleChange}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {formData.maintenanceFrequency === 'custom' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="maintenanceCycleMonths">Months</label>
                        <input
                          type="number"
                          id="maintenanceCycleMonths"
                          name="maintenanceCycleMonths"
                          value={formData.maintenanceCycleMonths || ''}
                          onChange={handleChange}
                          placeholder="e.g., 3"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="maintenanceCycleYears">Years</label>
                        <input
                          type="number"
                          id="maintenanceCycleYears"
                          name="maintenanceCycleYears"
                          value={formData.maintenanceCycleYears || ''}
                          onChange={handleChange}
                          placeholder="e.g., 1"
                          min="0"
                        />
                      </div>
                    </div>
                  )}

                  {formData.maintenanceFrequency && formData.maintenanceFrequency !== '' && (
                    <div className="form-group">
                      <label htmlFor="maintenanceStartDate">Start Date *</label>
                      <input
                        type="date"
                        id="maintenanceStartDate"
                        name="maintenanceStartDate"
                        value={formData.maintenanceStartDate}
                        onChange={handleChange}
                        required={!!formData.maintenanceFrequency}
                      />
                      <small className="help-text">The first maintenance will be scheduled relative to this date</small>
                    </div>
                  )}

                  {formData.maintenanceStartDate && (
                    <div className="form-group checklist-section">
                      <label>🔍 Maintenance Checklist Items</label>
                      <p className="section-hint">Add items that need to be checked during maintenance</p>
                      
                      <div className="checklist-input-group">
                        <input
                          type="text"
                          value={maintenanceChecklistItem}
                          onChange={(e) => setMaintenanceChecklistItem(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddMaintenanceChecklistItem();
                            }
                          }}
                          placeholder="e.g., Check oil level, inspect filters"
                          className="checklist-input"
                        />
                        <button
                          type="button"
                          onClick={handleAddMaintenanceChecklistItem}
                          className="btn btn-secondary btn-small"
                        >
                          Add
                        </button>
                      </div>

                      {formData.maintenanceChecklists.length > 0 && (
                        <div className="checklist-items">
                          {formData.maintenanceChecklists.map((item, index) => (
                            <div key={index} className="checklist-item">
                              <span>{item.text}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveMaintenanceChecklistItem(index)}
                                className="btn-remove"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Tab 6: Compliance */}
          {currentTab === 6 && (
            <div className="form-section tab-content">
              <h3>⚖️ Compliance Schedule (For Non-Consumables)</h3>
              
              {formData.assetType === 'consumable' && (
                <p style={{ color: '#ef4444', fontWeight: '500', marginBottom: '1rem' }}>
                  ⓘ Compliance schedules only apply to non-consumable assets.
                </p>
              )}

              {formData.assetType === 'non-consumable' && (
                <>
                  <p className="section-hint">Set up automatic compliance date cycles for this asset</p>

                  <div className="form-group">
                    <label>Compliance Cycle Frequency</label>
                    <div className="frequency-options">
                      {[
                        { value: '', label: 'None' },
                        { value: 'weekly', label: 'Weekly (7 days)' },
                        { value: 'biweekly', label: 'Bi-Weekly (14 days)' },
                        { value: 'monthly', label: 'Monthly (30 days)' },
                        { value: 'quarterly', label: 'Quarterly (90 days)' },
                        { value: 'semi_annual', label: 'Semi-Annual (180 days)' },
                        { value: 'yearly', label: 'Yearly (365 days)' },
                        { value: 'custom', label: 'Custom' }
                      ].map(opt => (
                        <label key={opt.value} className={`frequency-chip ${formData.complianceFrequency === opt.value ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="complianceFrequency"
                            value={opt.value}
                            checked={formData.complianceFrequency === opt.value}
                            onChange={handleChange}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {formData.complianceFrequency === 'custom' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="complianceCycleMonths">Months</label>
                        <input
                          type="number"
                          id="complianceCycleMonths"
                          name="complianceCycleMonths"
                          value={formData.complianceCycleMonths || ''}
                          onChange={handleChange}
                          placeholder="e.g., 3"
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="complianceCycleYears">Years</label>
                        <input
                          type="number"
                          id="complianceCycleYears"
                          name="complianceCycleYears"
                          value={formData.complianceCycleYears || ''}
                          onChange={handleChange}
                          placeholder="e.g., 1"
                          min="0"
                        />
                      </div>
                    </div>
                  )}

                  {formData.complianceFrequency && (
                    <div className="form-group">
                      <label htmlFor="complianceStartDate">Start Date *</label>
                      <input
                        type="date"
                        id="complianceStartDate"
                        name="complianceStartDate"
                        value={formData.complianceStartDate}
                        onChange={handleChange}
                        required={!!formData.complianceFrequency}
                      />
                      <small className="help-text">The first compliance check will be scheduled relative to this date</small>
                    </div>
                  )}

                  {formData.complianceStartDate && (
                    <div className="form-group checklist-section">
                      <label>✓ Compliance Checklist Items</label>
                      <p className="section-hint">Add checklist items that need to be verified for compliance</p>
                      
                      <div className="checklist-input-group">
                        <input
                          type="text"
                          value={complianceChecklistItem}
                          onChange={(e) => setComplianceChecklistItem(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddComplianceChecklistItem();
                            }
                          }}
                          placeholder="e.g., Verify certifications, Check equipment integrity"
                          className="checklist-input"
                        />
                        <button
                          type="button"
                          onClick={handleAddComplianceChecklistItem}
                          className="btn btn-secondary btn-small"
                        >
                          Add
                        </button>
                      </div>

                      {formData.complianceChecklists.length > 0 && (
                        <div className="checklist-items">
                          {formData.complianceChecklists.map((item, index) => (
                            <div key={index} className="checklist-item">
                              <span>{item.text}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveComplianceChecklistItem(index)}
                                className="btn-remove"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Navigation and Submit Buttons */}
          <div className="form-actions-tabs">
            <button
              type="button"
              onClick={() => setCurrentTab(Math.max(0, currentTab - 1))}
              className="btn btn-secondary"
              disabled={currentTab === 0}
            >
              ← Previous
            </button>

            <div style={{ flex: 1, textAlign: 'center', fontSize: '12px', color: '#64748b' }}>
              Step {currentTab + 1} of {tabs.length}
            </div>

            <button
              type="button"
              onClick={handleSaveForm}
              className="btn btn-secondary"
              title="Save form progress to browser (can be resumed later)"
            >
              💾 Save
            </button>

            {currentTab < tabs.length - 1 ? (
              <button
                type="button"
                onClick={() => setCurrentTab(Math.min(tabs.length - 1, currentTab + 1))}
                className="btn btn-secondary"
              >
                Next →
              </button>
            ) : (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Adding Asset...' : 'Add Asset'}
              </button>
            )}
          </div>
        </form>

        {planLimitPopup.visible && ReactDOM.createPortal(
          <div className="plan-limit-popup-overlay" onClick={closePlanLimitPopup}>
            <div className="plan-limit-popup-card" onClick={(e) => e.stopPropagation()}>
              <h3>{planLimitPopup.title || 'Plan Action Required'}</h3>
              <p>{planLimitPopup.message || 'Your plan limit is reached. Please renew or upgrade to continue.'}</p>
              <div className="plan-limit-popup-actions">
                <button
                  type="button"
                  className="plan-limit-btn primary"
                  onClick={() => {
                    closePlanLimitPopup();
                    navigate('/lab-admin/dashboard?section=view-plans');
                  }}
                >
                  Renew / Upgrade Plan
                </button>
                <button
                  type="button"
                  className="plan-limit-btn secondary"
                  onClick={() => {
                    closePlanLimitPopup();
                    navigate('/lab-admin/dashboard?section=view-plans');
                  }}
                >
                  View More Plans
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default AddAsset;
